import { useCallback, useEffect, useState } from 'react';
import { Check, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { BUG_REPORT_PRIORITIES } from '@/lib/bug-report';

type Task = {
  receipt_id: string;
  title: string;
  priority: string;
  column_id: string;
  done: boolean;
  reported_at: string | null;
  updated_at: string | null;
};
type Board = {
  columns: { id: string; label: string }[];
  tasks: Task[];
  missing: { receipt_id: string; title: string; reported_at: string }[];
  truncated: boolean;
};
const date = (value: string | null) =>
  value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleDateString('de-CH') : '–';

export function BugStatusBoard({
  scope = 'mine',
  revision = 0,
}: {
  scope?: 'mine' | 'all';
  revision?: number;
}) {
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get<{ success: boolean; data?: Board; error?: string }>(
          `/bug-reports/board?scope=${scope}`,
        );
        if (!res.success || !res.data) throw new Error(res.error || 'Status momentan nicht abrufbar.');
        if (!signal.aborted) setBoard(res.data);
      } catch (e) {
        if (!signal.aborted) setError(e instanceof Error ? e.message : 'Status momentan nicht abrufbar.');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [scope],
  );
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, revision, refresh]);

  return (
    <section className="space-y-4" aria-label="Status der Bugmeldungen">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold">{scope === 'all' ? 'Alle Meldungen' : 'Meine Meldungen'}</h3>
        <Button variant="outline" disabled={loading} onClick={() => setRefresh((v) => v + 1)}>
          <RefreshCw className={loading ? 'animate-spin' : ''} />
          Aktualisieren
        </Button>
      </div>
      {loading && !board && (
        <p role="status" className="text-sm text-muted">
          Status wird geladen…
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {board && (
        <>
          {board.truncated && (
            <p className="text-sm text-muted">Es werden nur die neuesten Meldungen angezeigt.</p>
          )}
          {!board.tasks.length && !board.missing.length && (
            <p className="text-sm text-muted">
              {scope === 'mine' ? 'Du hast noch keine sichtbaren Meldungen.' : 'Keine Meldungen vorhanden.'}
            </p>
          )}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {board.columns.map((column) => {
              const tasks = board.tasks
                .filter((t) => t.column_id === column.id)
                .sort((a, b) => (b.reported_at || '').localeCompare(a.reported_at || ''));
              return (
                <div key={column.id} className="min-w-0 rounded-lg border border-border bg-background p-3">
                  <h4 className="mb-3 flex justify-between gap-2 text-sm font-medium">
                    <span>{column.label}</span>
                    <span className="text-muted">{tasks.length}</span>
                  </h4>
                  <div className="space-y-2">
                    {tasks.length ? (
                      tasks.map((task) => (
                        <article
                          key={task.receipt_id}
                          className="space-y-2 rounded-md border border-border bg-card p-3"
                        >
                          <p className="break-words text-sm font-medium">{task.title}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                            <span className="rounded border border-border px-1.5 py-0.5">
                              {BUG_REPORT_PRIORITIES.find((p) => p.value === task.priority)?.label ||
                                'Mittel'}
                            </span>
                            {task.done && (
                              <span className="inline-flex items-center gap-1">
                                <Check size={14} />
                                Erledigt
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted">Gemeldet am {date(task.reported_at)}</p>
                          {task.updated_at && (
                            <p className="text-xs text-muted">Aktualisiert am {date(task.updated_at)}</p>
                          )}
                        </article>
                      ))
                    ) : (
                      <p className="text-sm text-muted">Keine Meldungen</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!!board.missing.length && (
            <div className="rounded-md border border-border p-3">
              <h4 className="mb-2 text-sm font-medium">Nicht mehr im Board sichtbar</h4>
              <ul className="space-y-1 text-sm text-muted">
                {board.missing.map((item) => (
                  <li className="break-words" key={item.receipt_id}>
                    {item.title} · {date(item.reported_at)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
